/** Gerador de documentos: modelos de texto com {{campos}}, preparação (o cadastro preenche, o resto vira pergunta) e geração em PDF. */
import { z } from 'zod';
import { Id } from './comum.js';

export const CategoriaModeloTexto = z.enum(['TRABALHISTA', 'ESCRITORIO', 'DECLARACAO', 'OUTRO']);
export type CategoriaModeloTexto = z.infer<typeof CategoriaModeloTexto>;
export const ROTULO_CATEGORIA_MODELO: Record<CategoriaModeloTexto, string> = { TRABALHISTA: 'Trabalhista', ESCRITORIO: 'Do escritório', DECLARACAO: 'Declaração', OUTRO: 'Outros' };

export const ModeloTexto = z.object({ id: Id, nome: z.string(), categoria: CategoriaModeloTexto, precisaFuncionario: z.boolean(), corpo: z.string(), padraoCodigo: z.string().nullable(), ativo: z.boolean(), atualizadoEm: z.string() });
export type ModeloTexto = z.infer<typeof ModeloTexto>;
export const ModeloTextoBody = z.object({
  nome: z.string().trim().min(2).max(120), categoria: CategoriaModeloTexto.default('OUTRO'), precisaFuncionario: z.boolean().default(false),
  corpo: z.string().trim().min(20, 'o texto do modelo está muito curto').max(40_000), ativo: z.boolean().default(true),
});
export type ModeloTextoBody = z.infer<typeof ModeloTextoBody>;

export const CampoDisponivel = z.object({ chave: z.string(), rotulo: z.string(), grupo: z.string() });
export type CampoDisponivel = z.infer<typeof CampoDisponivel>;

export const GeradorPrepararBody = z.object({
  /** o modelo do escritório — ou, sem ele, o código de um modelo padrão (vale o do escritório que veio dele; senão, a minuta embutida) */
  modeloId: Id.nullable().default(null), padraoCodigo: z.string().trim().max(60).nullable().default(null),
  clienteId: Id, funcionarioId: Id.nullable().default(null),
  /** documento de uma alteração contratual: a vigência e o que muda já entram no texto, e o cadastro é lido como ficará DEPOIS dela */
  alteracaoId: Id.nullable().default(null),
});
export type GeradorPrepararBody = z.infer<typeof GeradorPrepararBody>;
export const GeradorPergunta = z.object({
  chave: z.string(), rotulo: z.string(), longo: z.boolean(),
  /** é um dado que o sistema saberia preencher, mas está em branco no cadastro */
  faltaNoCadastro: z.boolean(),
});
export type GeradorPergunta = z.infer<typeof GeradorPergunta>;
export const GeradorPreparado = z.object({ modeloId: Id.nullable().optional(), titulo: z.string(), /** o cadastro já entrou; o que falta continua como {{campo}} */ texto: z.string(), perguntas: z.array(GeradorPergunta) });
export type GeradorPreparado = z.infer<typeof GeradorPreparado>;

const Texto = z.string().trim().min(20, 'o documento está vazio').max(60_000);
export const GeradorPreviaBody = z.object({ titulo: z.string().trim().min(2).max(120), texto: Texto });
export const GeradorGerarBody = z.object({
  clienteId: Id, funcionarioId: Id.nullable().default(null), titulo: z.string().trim().min(2).max(120), texto: Texto,
  /** aparece no portal do cliente */
  publicarNoPortal: z.boolean().default(false),
  /** com o documento no portal: pede a assinatura eletrônica do contato do cliente */
  pedirAssinatura: z.boolean().default(false),
  /** o documento é o aditivo (ou contrato) desta alteração contratual: fica ligado ao registro */
  alteracaoId: Id.nullable().default(null),
});
export type GeradorGerarBody = z.infer<typeof GeradorGerarBody>;
export const GeradorGerado = z.object({ anexoId: Id, nome: z.string() });
export type GeradorGerado = z.infer<typeof GeradorGerado>;
/**
 * A ficha cadastral tem duas versões: a do ESCRITÓRIO (com código interno, responsáveis, obrigações, honorário) e a do
 * CLIENTE — a que ele manda a fornecedores, bancos e guarda para consulta: só os dados da empresa, sem nada interno.
 */
export const FichaClienteOpcoes = z.object({
  para: z.enum(['escritorio', 'cliente']).default('escritorio'),
  socios: z.boolean().default(true), banco: z.boolean().default(true), contatos: z.boolean().default(false),
  /** mostra o escritório como a contabilidade responsável */
  contabilidade: z.boolean().default(true),
});
export type FichaClienteOpcoes = z.infer<typeof FichaClienteOpcoes>;
const Flag = (padrao: '0' | '1') => z.enum(['0', '1']).default(padrao).transform((v) => v === '1');
/** as mesmas opções na URL do PDF (GET) */
export const FichaClienteQuery = z.object({ para: z.enum(['escritorio', 'cliente']).default('escritorio'), socios: Flag('1'), banco: Flag('1'), contatos: Flag('0'), contabilidade: Flag('1') });
export const FichaSalvarBody = FichaClienteOpcoes.extend({ publicarNoPortal: z.boolean().default(false) });
